import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Tag, Plus, Search, Edit, Trash2, X, Check, AlertCircle, RefreshCw
} from 'lucide-react';
import toast from 'react-hot-toast';
import tagService from '../services/tagService';
import FilterBar from '../components/FilterBar';
import EnhancedDataTable from '../components/EnhancedDataTable';
import ResponsiveModal, { 
  ResponsiveForm, 
  ResponsiveInput, 
  ResponsiveButton 
} from '../components/ResponsiveModal';
import { CreateButton, SaveButton, UpdateButton, DeleteButton, CancelButton } from '../components/CommonButtons';
import CommonLoader, { PageLoader } from '../components/CommonLoader';

const Tags = () => {
  const { dataroomUuid } = useParams();
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTagType, setSelectedTagType] = useState('');
  const [pagination, setPagination] = useState({
    current_page: 1,
    per_page: 15,
    total: 0,
    last_page: 1
  });

  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedTag, setSelectedTag] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    tag_type: 'file'
  });
  const [formErrors, setFormErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (dataroomUuid) {
      fetchTags();
    }
  }, [dataroomUuid, pagination.current_page, searchTerm, selectedTagType]);

  const fetchTags = async () => {
    try {
      setLoading(true);
      const params = {
        page: pagination.current_page,
        per_page: pagination.per_page,
        ...(searchTerm && { search: searchTerm }),
        ...(selectedTagType && { tag_type: selectedTagType })
      };

      const response = await tagService.getTags(dataroomUuid, params);
      
      // Handle the API response structure
      if (response && response.tags && Array.isArray(response.tags)) {
        setTags(response.tags);
        
        // Set pagination from the response
        if (response.pagination) {
          setPagination({
            current_page: response.pagination.current_page,
            per_page: response.pagination.per_page,
            total: response.pagination.total,
            last_page: response.pagination.last_page
          });
        }
      } else {
        // Fallback for unexpected response structure
        setTags([]);
        setPagination({
          current_page: 1,
          per_page: 15,
          total: 0,
          last_page: 1
        });
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to load tags');
      setTags([]); // Ensure tags is always an array even on error
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setFormData({ name: '', tag_type: 'file' });
    setFormErrors({});
    setShowCreateModal(true);
  };

  const handleEdit = (tag) => {
    if (!tag) return;
    setSelectedTag(tag);
    setFormData({
      name: tag.name || '',
      tag_type: tag.tag_type || 'file'
    });
    setFormErrors({});
    setShowEditModal(true);
  };

  const handleDelete = (tag) => {
    if (!tag) return;
    setSelectedTag(tag);
    setShowDeleteModal(true);
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.name.trim()) {
      errors.name = 'Tag name is required';
    }
    if (!formData.tag_type) {
      errors.tag_type = 'Tag type is required';
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmitCreate = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsSubmitting(true);
    try {
      await tagService.createTag(dataroomUuid, formData);
      toast.success('Tag created successfully');
      setShowCreateModal(false);
      fetchTags();
    } catch (error) {
      if (error.response?.data?.errors) {
        setFormErrors(error.response.data.errors);
      } else {
        toast.error(error.response?.data?.message || 'Failed to create tag');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmitEdit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsSubmitting(true);
    try {
      await tagService.updateTag(dataroomUuid, selectedTag.id, formData);
      toast.success('Tag updated successfully');
      setShowEditModal(false);
      fetchTags();
    } catch (error) {
      if (error.response?.data?.errors) {
        setFormErrors(error.response.data.errors);
      } else {
        toast.error(error.response?.data?.message || 'Failed to update tag');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleConfirmDelete = async () => {
    setIsSubmitting(true);
    try {
      await tagService.deleteTag(dataroomUuid, selectedTag.id);
      toast.success('Tag deleted successfully');
      setShowDeleteModal(false);
      fetchTags();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to delete tag');
    } finally {
      setIsSubmitting(false);
    }
  };

  const columns = [
    {
      key: 'name',
      header: 'Tag Name',
      sortable: true,
      render: (value, tag) => {
        if (!tag) return <span className="text-gray-400">N/A</span>;
        return (
          <div className="flex items-center gap-2">
            <Tag className="w-4 h-4 text-gray-500" />
            <span className="font-medium">{tag.name || 'N/A'}</span>
          </div>
        );
      }
    },
    {
      key: 'tag_type',
      header: 'Type',
      sortable: true,
      render: (value, tag) => {
        if (!tag || !tag.tag_type) return <span className="text-gray-400">N/A</span>;
        return (
          <span className={`px-2 py-1 text-xs rounded-full ${
            tag.tag_type === 'file' 
              ? 'bg-blue-100 text-blue-700' 
              : 'bg-green-100 text-green-700'
          }`}>
            {tag.tag_type}
          </span>
        );
      }
    },
    {
      key: 'created_by',
      header: 'Created By',
      sortable: false,
      render: (value, tag) => {
        if (!tag) return <span className="text-gray-400">N/A</span>;
        return (
          <div className="text-sm">
            {tag.created_by ? (
              <div>
                <div className="font-medium text-gray-900">
                  {tag.created_by.first_name || ''} {tag.created_by.last_name || ''}
                </div>
                <div className="text-gray-500">{tag.created_by.email || 'N/A'}</div>
              </div>
            ) : (
              <span className="text-gray-400">N/A</span>
            )}
          </div>
        );
      }
    },
    {
      key: 'created_at',
      header: 'Created Date',
      sortable: true,
      render: (value, tag) => {
        if (!tag || !tag.created_at) return <span className="text-gray-400">N/A</span>;
        return new Date(tag.created_at).toLocaleDateString();
      }
    },
    {
      key: 'updated_at',
      header: 'Last Updated',
      sortable: true,
      render: (value, tag) => {
        if (!tag || !tag.updated_at) return <span className="text-gray-400">N/A</span>;
        return new Date(tag.updated_at).toLocaleDateString();
      }
    }
  ];

  // Handle sorting
  const handleSort = (key, direction) => {
    const sorted = [...tags].sort((a, b) => {
      const aVal = a[key];
      const bVal = b[key];
      if (aVal < bVal) return direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return direction === 'asc' ? 1 : -1;
      return 0;
    });
    setTags(sorted);
  };

  const actions = (tag) => {
    if (!tag) return [];
    return [
      {
        icon: Edit,
        label: 'Edit',
        onClick: () => handleEdit(tag),
        className: 'text-blue-600 hover:text-blue-800'
      },
      {
        icon: Trash2,
        label: 'Delete',
        onClick: () => handleDelete(tag),
        className: 'text-red-600 hover:text-red-800'
      }
    ];
  };

  if (loading && tags.length === 0) {
    return <PageLoader />;
  }

  return (
    <div>
      {/* Page Header */}
      <div className="mb-8 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Tags Management</h1>
          <p className="text-sm text-gray-600 mt-1">Manage tags for your dataroom</p>
        </div>
        <button
          onClick={fetchTags}
          className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
          title="Refresh"
        >
          <RefreshCw className="w-5 h-5" />
        </button>
      </div>

      {/* Filter Bar */}
      <FilterBar
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        searchPlaceholder="Search tags..."
        filters={[
          {
            label: "Tag Type",
            type: "select",
            value: selectedTagType,
            onChange: setSelectedTagType,
            options: [
              { value: "file", label: "File" },
              { value: "invitation", label: "Invitation" }
            ]
          }
        ]}
        onClearFilters={() => {
          setSearchTerm('');
          setSelectedTagType('');
        }}
      >
        <CreateButton onClick={handleCreate}>
          Create Tag
        </CreateButton>
      </FilterBar>

      {/* Tags Table */}
      <EnhancedDataTable
        columns={columns}
        data={tags}
        actions={actions}
        loading={loading}
        emptyMessage="No tags found"
        pagination={pagination}
        onPageChange={(page) => setPagination(prev => ({ ...prev, current_page: page }))}
        itemName="tags"
        onSort={handleSort}
        defaultSortKey="created_at"
        defaultSortDirection="desc"
        showColumnControls={true}
        showRefresh={true}
        onRefresh={fetchTags}
      />

      {/* Create Tag Modal */}
      <ResponsiveModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        title="Create New Tag"
      >
        <ResponsiveForm onSubmit={handleSubmitCreate}>
          <ResponsiveInput
            label="Tag Name"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            error={formErrors.name}
            placeholder="Enter tag name"
            required
          />
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Tag Type
            </label>
            <select
              value={formData.tag_type}
              onChange={(e) => setFormData({ ...formData, tag_type: e.target.value })}
              className={`w-full px-3 py-1.5 text-sm border ${formErrors.tag_type ? 'border-red-500' : 'border-gray-300'} rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
              required
            >
              <option value="file">File</option>
              <option value="invitation">Invitation</option>
            </select>
            {formErrors.tag_type && (
              <p className="mt-1 text-sm text-red-600">{formErrors.tag_type}</p>
            )}
          </div>

          <div className="flex gap-3 pt-4">
            <SaveButton
              type="submit"
              loading={isSubmitting}
            >
              Create Tag
            </SaveButton>
            <CancelButton
              type="button"
              onClick={() => setShowCreateModal(false)}
            />
          </div>
        </ResponsiveForm>
      </ResponsiveModal>

      {/* Edit Tag Modal */}
      <ResponsiveModal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        title="Edit Tag"
      >
        <ResponsiveForm onSubmit={handleSubmitEdit}>
          <ResponsiveInput
            label="Tag Name"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            error={formErrors.name}
            placeholder="Enter tag name"
            required
          />
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Tag Type
            </label>
            <select
              value={formData.tag_type}
              onChange={(e) => setFormData({ ...formData, tag_type: e.target.value })}
              className={`w-full px-3 py-1.5 text-sm border ${formErrors.tag_type ? 'border-red-500' : 'border-gray-300'} rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
              required
            >
              <option value="file">File</option>
              <option value="invitation">Invitation</option>
            </select>
            {formErrors.tag_type && (
              <p className="mt-1 text-sm text-red-600">{formErrors.tag_type}</p>
            )}
          </div>

          <div className="flex gap-3 pt-4">
            <UpdateButton
              type="submit"
              loading={isSubmitting}
            >
              Update Tag
            </UpdateButton>
            <CancelButton
              type="button"
              onClick={() => setShowEditModal(false)}
            />
          </div>
        </ResponsiveForm>
      </ResponsiveModal>

      {/* Delete Confirmation Modal */}
      <ResponsiveModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        title="Delete Tag"
      >
        <div className="space-y-4">
          <div className="flex items-center gap-3 p-4 bg-red-50 rounded-lg">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
            <p className="text-sm text-red-800">
              Are you sure you want to delete the tag "{selectedTag?.name}"? This action cannot be undone.
            </p>
          </div>
          
          <div className="flex gap-3">
            <DeleteButton
              onClick={handleConfirmDelete}
              loading={isSubmitting}
            >
              Delete Tag
            </DeleteButton>
            <CancelButton
              onClick={() => setShowDeleteModal(false)}
            />
          </div>
        </div>
      </ResponsiveModal>
    </div>
  );
};

export default Tags;